/**
 * shrinkImage(blob) - a picture made small in the browser, as a JPEG data:
 * URL: at most PHOTO_MAX px on its longer side, JPEG quality PHOTO_QUALITY.
 * No library: a canvas does it. Used for engineer photos (a phone photo was
 * stored at ~1.4 MB; a 400 px card picture is ~30-60 KB).
 *
 * Rejects when the picture cannot be read or drawn (a canvas-less test
 * environment, a file that is not an image); callers keep the original then.
 */
export const PHOTO_MAX = 400;
export const PHOTO_QUALITY = 0.85;
// a stored photo bigger than this is worth shrinking (the Engineers tab offers it)
export const PHOTO_BIG_BYTES = 150 * 1024;

/** The size a picture of w x h is drawn at so its longer side is at most `max`. */
export function fitWithin(w, h, max = PHOTO_MAX) {
  const scale = Math.min(1, max / Math.max(w || 1, h || 1));
  return { width: Math.max(1, Math.round(w * scale)), height: Math.max(1, Math.round(h * scale)) };
}

const loadImage = (blob) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('not a readable image'));
    };
    img.src = url;
  });

export async function shrinkImage(blob, { max = PHOTO_MAX, quality = PHOTO_QUALITY } = {}) {
  if (!blob || typeof URL.createObjectURL !== 'function') throw new Error('no image');
  const img = await loadImage(blob);
  const { width, height } = fitWithin(img.naturalWidth || img.width, img.naturalHeight || img.height, max);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext && canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.fillStyle = '#fff'; // a transparent PNG would turn black as a JPEG
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);
  const out = canvas.toDataURL('image/jpeg', quality);
  if (!/^data:image\/jpeg/.test(out)) throw new Error('could not encode');
  return out;
}

/** A File / Blob as a data: URL, unchanged (the fallback when shrinking fails). */
export const readAsDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (ev) => resolve(ev.target.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
